import { makeStyles, tokens, Body1 } from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';

/* Shared page header banner — the Transformr "executive hero" in primary green.
   Standing rules: kicker is pale brand green (greenPale — a brighter tint of the
   primary), subheading is light gray (16px). Use at the top of every page so the
   chrome reads as one product. */

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXXL,
    flexWrap: 'wrap',
    backgroundColor: transformrBrand.green,
    color: transformrBrand.white,
    borderRadius: tokens.borderRadiusXLarge,
    // Design padding ~34/38px — no spacing token that large, so rem.
    padding: '2.125rem 2.375rem 2rem',
    // ~50% softer than tokens.shadow28 (alphas halved).
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.06), 0 14px 28px rgba(0, 0, 0, 0.07)',
    overflow: 'hidden',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  kicker: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: transformrBrand.greenPale,
  },
  title: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.01em',
    margin: 0,
    color: transformrBrand.white,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase400,
    color: transformrBrand.grey200,
    maxWidth: '46rem',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

export interface PageBannerProps {
  kicker: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional icon rendered before the kicker text. */
  icon?: ReactNode;
  /** Optional right-aligned content (buttons, status). */
  actions?: ReactNode;
}

export function PageBanner({ kicker, title, subtitle, icon, actions }: PageBannerProps) {
  const styles = useStyles();
  return (
    <div className={styles.banner}>
      <div className={styles.text}>
        <span className={styles.kicker}>{icon}{kicker}</span>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <Body1 className={styles.subtitle}>{subtitle}</Body1>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
